import axios, { InternalAxiosRequestConfig } from 'axios';
import { AxiosError } from 'axios';

let token: string | null = null;
let refreshToken: string | null = null;
// Shared by all callers so only one refresh request can be in flight at a time.
let refreshPromise: Promise<string> | null = null;

const setApiClientToken = (newToken: string) => {
  token = newToken;
};

const setApiClientRefreshToken = (newRefreshToken: string) => {
  refreshToken = newRefreshToken;
};

const initializeTokens = (initialToken: string | null, initialRefreshToken: string | null) => {
  token = initialToken;
  refreshToken = initialRefreshToken;
};

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

// Token renewal uses a bare client so it cannot be intercepted by its own refresh flow.
const authClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    // If a refresh is already running, queue new API requests behind it so they use the fresh token.
    if (refreshPromise && !isAuthUrl(config.url)) {
      await refreshPromise;
    }

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error)
);

function isAuthUrl(url?: string) {
    if (!url) return false;
    const normalizedUrl = url.toLowerCase();
    return (
        normalizedUrl.includes("authentication/login") ||
        normalizedUrl.includes("authentication/renew")
    );
}

apiClient.interceptors.response.use(
  response => response, // Simply return the response for successful requests
    async (error) => {  // Try to renew token on 401 errors       
        const originalRequest = error.config;

        const status = error.response?.status;
        const url = originalRequest?.url;

        if (!status || status !== 401 || !originalRequest) {
            return Promise.reject(error);
        }

        if (isAuthUrl(url)) {            
            return Promise.reject(error);
        }

        if (originalRequest._retry) {
            return Promise.reject(error);
        }
        originalRequest._retry = true;


        try {
            const newToken = await renewToken();
            // Retry the failed request once with the access token returned by the shared refresh.
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`; // Update the token in the header
            return apiClient(originalRequest); // Retry the original request with the new token
        }
        catch (renewError)
        {                           
            return Promise.reject(renewError);
        }                  
  }
);

const renewToken = async (): Promise<string> => {
  // Reuse the existing refresh promise when several 401 responses arrive at the same time.
  if (!refreshPromise) {
    refreshPromise = fetchRenewedToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

const fetchRenewedToken = async (): Promise<string> => {
  // Capture the current refresh token before the await; the backend rotates it on success.
  const currentRefreshToken = refreshToken;
  if (!currentRefreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await authClient.post('/authentication/renew', { refreshToken: currentRefreshToken }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const newToken = response.data.accessToken;
    const newRefreshToken = response.data.refreshToken;
    setApiClientToken(newToken);
    setApiClientRefreshToken(newRefreshToken);
    return newToken;
  } catch (error) {
    console.error('Error renewing token:', error);
    throw error;
  }
};

export default apiClient;
export { setApiClientToken, setApiClientRefreshToken, initializeTokens };
